import { Form } from "antd"
import { Group, Title } from "components/common"
import { t } from "i18next"
import { dateTimeFunction } from "utils/date"
import PaymentProofImageUpload from "../../paymentProofImageUpload"
import RACheckbox from "./RACheckbox"
import AccountantSection from "./AccountantSection"
import { useEffect, useMemo, useRef } from "react"
import { hasPaymentProofValue, PAYMENTS_FORM_LIST_NAME } from "../utils/paymentUtils"

const ExtraFieldsSection = ({
  paymentModesMap,
  form,
  getAccountant,
  accountantData,
  isLoadingAccountant,
  platformKey,
  projectId,
  ocrEnabled,
  attachmentType,
  imageData,
  isRaEnabled,
  listFieldName,
}) => {
  const fullPrefix = [PAYMENTS_FORM_LIST_NAME, listFieldName]
  const depositSlip = Form.useWatch([...fullPrefix, "bank_deposit_slip"], form)
  const enableDepositRA = Form.useWatch([...fullPrefix, "enableDepositRA"], form)
  const extraFields = paymentModesMap?.[attachmentType]?.extraFields

  const depositSlipMapKey = useMemo(() => {
    if (!depositSlip) return ""
    const fileId =
      depositSlip?.url ||
      depositSlip?.name ||
      depositSlip?.fileList?.[0]?.uid ||
      depositSlip?.fileList?.[0]?.name ||
      ""
    if (!fileId) return ""
    const img = depositSlip?.imageData
    if (!img) return `${fileId}|pending`
    const imgPart = img?.id ?? img?.chequeNumber ?? img?.transactionReferenceNumber ?? "parsed"
    return `${fileId}|${imgPart}`
  }, [
    depositSlip?.url,
    depositSlip?.name,
    depositSlip?.fileList,
    depositSlip?.imageData?.id,
    depositSlip?.imageData?.chequeNumber,
    depositSlip?.imageData?.transactionReferenceNumber,
  ])

  const lastDepositSlipMapKeyRef = useRef(null)

  useEffect(() => {
    if (!depositSlip) {
      lastDepositSlipMapKeyRef.current = null
      return
    }
    if (!depositSlipMapKey) return
    if (lastDepositSlipMapKeyRef.current === depositSlipMapKey) return
    lastDepositSlipMapKeyRef.current = depositSlipMapKey

    if (depositSlip?.imageData?.chequeType) {
      form.setFieldValue([...fullPrefix, "paymentType"], depositSlip?.imageData?.chequeType)
    }
    const depositSlipField =
      ["cheque", "cash", "bank_order"].includes(attachmentType) &&
      paymentModesMap?.[attachmentType]?.meta?.depositSlip

    if (depositSlipField && depositSlip?.imageData) {
      Object.keys(depositSlipField).forEach(key => {
        const sourceKey = depositSlipField[key]
        const raw = depositSlip?.imageData?.[sourceKey]
        let value
        if (sourceKey === "date") {
          value = raw == null || raw === "null" ? "" : dateTimeFunction({ date: raw, Default: true })
        } else if (raw == null || raw === "null") {
          value = ""
        } else if (typeof raw === "object" && raw !== null && !(raw instanceof Date)) {
          value = raw?.key
        } else {
          value = raw
        }
        form.setFieldValue([...fullPrefix, key], value)
      })
    }
  }, [depositSlipMapKey, depositSlip, attachmentType, paymentModesMap, form, fullPrefix])

  const hasBankDepositSlip = extraFields?.["bank_deposit_slip"]
  const hasReceipt = extraFields?.["receipt"]

  if (!hasBankDepositSlip && !hasReceipt) return null

  return (
    <>
      <Title type="secondary" level={4}>
        {hasBankDepositSlip ? t("Bank Deposit Slip Details") : t("Receipt Details")}
      </Title>
      {!!isRaEnabled && hasBankDepositSlip && !depositSlip?.fileList?.length && (
        <RACheckbox listFieldName={listFieldName} fieldName="enableDepositRA" getAccountant={getAccountant} />
      )}

      {!!enableDepositRA ? (
        <AccountantSection
          form={form}
          accountantData={accountantData}
          isLoadingAccountant={isLoadingAccountant}
          platformKey={platformKey}
          projectId={projectId}
          listFieldName={listFieldName}
        />
      ) : (
        <Group template="repeat(2, 1fr)" gap="6px 32px">
          {!!hasBankDepositSlip && (
            <Form.Item
              name={[listFieldName, "bank_deposit_slip"]}
              label={t("Bank Deposit Slip")}
              required
              rules={[
                {
                  validator: (_, value) =>
                    hasPaymentProofValue(value) || !!value?.imageData
                      ? Promise.resolve()
                      : Promise.reject(t("Bank Deposit Slip is Required")),
                },
              ]}
            >
              <PaymentProofImageUpload
                setFiles={data => form.setFieldValue([...fullPrefix, "bank_deposit_slip"], data)}
                paymentProofProps={{
                  attachmentTypeId: hasBankDepositSlip?.attachmentTypeId,
                  api: ocrEnabled,
                  visionAllowedonProject: !!ocrEnabled,
                  detectPaymentMode: false,
                  date: imageData?.imageData?.date,
                }}
                platformKey={platformKey}
                projectId={projectId}
              />
            </Form.Item>
          )}
          {!!hasReceipt && (
            <Form.Item
              name={[listFieldName, "receipt"]}
              label={t("Receipt")}
              colon={false}
              required={hasReceipt?.mandatory}
              rules={[
                {
                  validator: (_, value) =>
                    hasReceipt?.mandatory && (!value || !value?.length)
                      ? Promise.reject(t("Receipt is Required"))
                      : Promise.resolve(),
                },
              ]}
            >
              <PaymentProofImageUpload
                setFiles={data => form.setFieldValue([...fullPrefix, "receipt"], data)}
                paymentProofProps={{
                  attachmentTypeId: hasReceipt?.attachmentTypeId,
                  api: false,
                  visionAllowedonProject: false,
                  detectPaymentMode: false,
                }}
                platformKey={platformKey}
                projectId={projectId}
              />
            </Form.Item>
          )}
        </Group>
      )}
    </>
  )
}

export default ExtraFieldsSection
